import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useToast } from "hooks/useToast";
import {
  AdminPerk,
  PerkTag,
  useAddPerkTagMutation,
  useRemovePerkTagMutation,
} from "store/apis/perksCrm";
import { TagChip } from "./TagChip";

type ManageTagsModalProps = {
  perk: AdminPerk;
  allTags?: PerkTag[];
  onClose: () => void;
};

export const ManageTagsModal = ({
  perk,
  allTags,
  onClose,
}: ManageTagsModalProps) => {
  const { successToast, alertToast } = useToast();
  const [addPerkTag] = useAddPerkTagMutation();
  const [removePerkTag] = useRemovePerkTagMutation();

  const assignedTagIds = new Set((perk.tags || []).map((tag) => tag.uuid));
  const availableTags = (allTags || []).filter(
    (tag) => !assignedTagIds.has(tag.uuid)
  );

  const handleAdd = async (tagId: string) => {
    try {
      await addPerkTag({ perkId: perk.perk_id, tagId }).unwrap();
      successToast({ message: "Tag added" });
    } catch {
      alertToast({ message: "Failed to add tag" });
    }
  };

  const handleRemove = async (tagId: string) => {
    try {
      await removePerkTag({ perkId: perk.perk_id, tagId }).unwrap();
      successToast({ message: "Tag removed" });
    } catch {
      alertToast({ message: "Failed to remove tag" });
    }
  };

  return (
    <Modal.Root open onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Manage Tags — {perk.name}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-4">
            <div>
              <p className="t-text-body-sm t-font-medium t-text-text-60 t-mb-2">
                Assigned tags
              </p>
              <div className="t-flex t-flex-wrap t-gap-2 t-min-h-8">
                {(perk.tags || []).length === 0 ? (
                  <span className="t-text-body-sm t-text-text-30">
                    No tags assigned
                  </span>
                ) : (
                  perk.tags.map((tag) => (
                    <TagChip key={tag.uuid} tag={tag} onRemove={handleRemove} />
                  ))
                )}
              </div>
            </div>
            <div>
              <p className="t-text-body-sm t-font-medium t-text-text-60 t-mb-2">
                Add tags
              </p>
              <div className="t-flex t-flex-wrap t-gap-2 t-min-h-8">
                {availableTags.length === 0 ? (
                  <span className="t-text-body-sm t-text-text-30">
                    All tags assigned
                  </span>
                ) : (
                  availableTags.map((tag) => (
                    <Button
                      key={tag.uuid}
                      type="button"
                      customType="text"
                      size="xsmall"
                      style={{
                        color: tag.color_hex,
                        backgroundColor: tag.bg_color_hex,
                      }}
                      onClick={() => handleAdd(tag.uuid)}
                    >
                      + #{tag.title}
                    </Button>
                  ))
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
